import React from "react";
import {
  Box,
  Typography,
  Grid2,
  Select,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Tooltip,
  InputAdornment,
  IconButton,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useFilterStore } from "../../Store/filterStore";
import { tokens } from "../../Common/designTokens"; // added
dayjs.extend(utc);

const gridStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 3,
  mb: 1,
};

export function MaturityDateFilter({
  minDate,
  maxDate,
  onMinDateChange,
  onMaxDateChange,
  onValidationChange,
  // New props to support yieldtable source preview/behavior
  previewFromDate,
  previewToDate,
  skipValidation = false,
  showYieldCellTag = false,
}) {
  // Check if float selection would be invalid (skipped if skipValidation)
  const minDateError = skipValidation ? false : (minDate !== null && maxDate !== null && minDate > maxDate);
  const maxDateError = skipValidation ? false : (minDate !== null && maxDate !== null && maxDate < minDate);
  const [activeField, setActiveField] = React.useState(null);
  const [datePreview, setDatePreview] = React.useState("");
  const setMaturityEditedByUser = useFilterStore((s) => s.setMaturityEditedByUser);

  // Calculate date preview: if skipValidation is true and preview dates exist, show them directly; else show computed range
  React.useEffect(() => {
    const calculateDatePreview = () => {
      // Yieldtable preview path
      if (skipValidation && (previewFromDate || previewToDate)) {
        const fromStr = previewFromDate ? dayjs(previewFromDate).isValid() ? dayjs(previewFromDate).format('D MMM YYYY') : previewFromDate : '';
        const toStr = previewToDate ? dayjs(previewToDate).isValid() ? dayjs(previewToDate).format('D MMM YYYY') : previewToDate : '';
        if (fromStr && toStr) return `(${fromStr} to ${toStr})`;
        if (fromStr) return `(${fromStr} to ...)`;
        if (toStr) return `(... to ${toStr})`;
        return "";
      }

      // Default computed preview based on numeric years
      if (minDate === null && maxDate === null) {
        return "";
      }
      const today = dayjs().utc();
      let fromDate = null;
      let toDate = null;

      // New logic: if maxDate < 1 (strictly), skip +/- 90 day buffer adjustments
      const useBuffer = !(maxDate !== null && maxDate < 1);

      if (minDate !== null) {
        const baseMin = today.add(minDate * 365, 'day');
        const adjustedMin = useBuffer ? baseMin.subtract(90, 'day') : baseMin;
        fromDate = adjustedMin.isBefore(today) ? today : adjustedMin;
      }
      if (maxDate !== null) {
        const baseMax = today.add(maxDate * 365, 'day');
        toDate = useBuffer ? baseMax.add(90, 'day') : baseMax;
      }

      const fromDateStr = fromDate ? fromDate.format('D MMM YYYY') : '';
      const toDateStr = toDate ? toDate.format('D MMM YYYY') : '';

      if (fromDateStr && toDateStr) return `(${fromDateStr} to ${toDateStr})`;
      if (fromDateStr) return `(${fromDateStr} to ...)`;
      if (toDateStr) return `(... to ${toDateStr})`;
      return "";
    };

    setDatePreview(calculateDatePreview());
  }, [minDate, maxDate, previewFromDate, previewToDate, skipValidation]);

  // Report validation status to parent
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(minDateError || maxDateError);
    }
  }, [minDateError, maxDateError]); // dont change these dependencies

  // Ensure minDate is set to 0 if maxDate is filled and minDate is empty (skip when in preview/skipValidation mode)
  React.useEffect(() => {
    if (skipValidation) return;
    if ((maxDate !== null && maxDate !== "") && (minDate === null || minDate === "")) {
      onMinDateChange(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDate, skipValidation]);
  // Show error only in the active field
  const showMinError = minDateError && (activeField === "min" || !activeField);
  const showMaxError = maxDateError && (activeField === "max" || !activeField);


  return (
    <Grid2 sx={gridStyle}>
    <Box sx={{ width: "188px" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Typography fontWeight="bold" sx={{ fontSize: "16px", lineHeight: 1, color: tokens.colors.fontColour }}>
          Maturity Range
        </Typography>
        {showYieldCellTag && (
          <Typography
            component="span"
            sx={{
              fontSize: "12px",
              opacity: 0.7,
              fontWeight: 400,
              lineHeight: 1,
              color: tokens.colors.fontColour85,
            }}
          >
            (Yield Cell)
          </Typography>
        )}
      </Box>
      {datePreview && (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: 500, mt: 0.5, color: tokens.colors.fontColour85 }}
        >
          {datePreview}
        </Typography>
      )}
    </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ width: "160px", position: "relative" }}>
          <NumericFormat
            fullWidth
            placeholder="Min"
            value={minDate === null ? "" : minDate}
            onValueChange={(values, sourceInfo) => {
              const { floatValue } = values;
              if (floatValue === undefined) {
                onMinDateChange(null);
              } else {
                onMinDateChange(Math.max(0, floatValue));
              }
              setActiveField("min");
              // Only mark as user edit when the source is an actual user event
              if (sourceInfo?.source === "event") {
                setMaturityEditedByUser(true);
              }
            }}
            onFocus={() => setActiveField("min")}
            error={showMinError}
            customInput={TextField}
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale={false}
            isAllowed={(values) => {
              const { floatValue } = values;
              if (floatValue === undefined) return true;
              return floatValue >= 0 && floatValue <= 50;
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {showMinError ? (
                    <Tooltip
                      title="Min value must be less than max value, not in the past, and not exceed 50 years"
                      placement="top"
                    >
                      <ErrorOutlineIcon
                        color="error"
                        sx={{
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Typography sx={{ ml: 1, mr: 2 }}>Yr</Typography>
      </Box>
      <Typography>To</Typography>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ width: "160px", position: "relative" }}>
          <NumericFormat
            fullWidth
            placeholder="Max"
            value={maxDate === null ? "" : maxDate}
            onValueChange={(values, sourceInfo) => {
              const { floatValue } = values;
              if (floatValue === undefined) {
                onMaxDateChange(null);
              } else {
                onMaxDateChange(floatValue);
              }
              setActiveField("max");
              // Only mark as user edit when the source is an actual user event
              if (sourceInfo?.source === "event") {
                setMaturityEditedByUser(true);
              }
            }}
            onFocus={() => setActiveField("max")}
            error={showMaxError}
            customInput={TextField}
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale={false}
            isAllowed={(values) => {
              const { floatValue } = values;
              if (floatValue === undefined) return true;
              return floatValue <= 50;
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {showMaxError ? (
                    <Tooltip
                      title="Max value must be greater than min value and not exceed 50 years"
                      placement="top"
                    >
                      <ErrorOutlineIcon
                        color="error"
                        sx={{
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Typography sx={{ ml: 1 }}>Yr</Typography>
      </Box>
    </Grid2>
  );
}

export function MinMaxCheckFilter({
  label,
  check,
  min,
  max,
  onCheckChange,
  onMinChange,
  onMaxChange,
  decimalPlaces = 0,
  onValidationChange,
}) {
  // Get validation limits based on field type
  const getValidationLimits = () => {
    switch (label) {
      case "Quantity":
        return { min: 0, max: 10000000, isInteger: true, decimalScale: 0 };
      case "Price":
        return { min: 0, max: 100000, isInteger: false, decimalScale: 5 };
      case "Coupon":
        return { min: 0, max: 100, isInteger: false, decimalScale: 5 };
      case "Yield To Worst":
        return { min: 0, max: 1000, isInteger: false, decimalScale: 5 };
      default:
        return {
          min: 0,
          max: Number.MAX_SAFE_INTEGER,
          isInteger: decimalPlaces === 0,
          decimalScale: decimalPlaces,
        };
    }
  };

  const limits = getValidationLimits();

  // Track which field is being edited
  const [activeField, setActiveField] = React.useState(null);

  // Only check min/max relationship error
  const hasMinMaxError = check && min !== null && max !== null && min > max;

  // Report validation status to parent
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasMinMaxError);
    }
  }, [check, min, max, hasMinMaxError]);

  // Get appropriate error messages
  const minError =
    hasMinMaxError && activeField === "min"
      ? "Min cannot be greater than Max"
      : "";
  const maxError =
    hasMinMaxError && activeField === "max"
      ? "Max cannot be less than Min"
      : "";

  // Value change handlers with field tracking
  const handleMinFocus = () => setActiveField("min");
  const handleMaxFocus = () => setActiveField("max");

  const handleMinValueChange = ({ floatValue }) => {
    setActiveField("min");
    onMinChange(floatValue === undefined ? null : floatValue);
  };

  const handleMaxValueChange = ({ floatValue }) => {
    setActiveField("max");
    onMaxChange(floatValue === undefined ? null : floatValue);
  };

  // When a change resolves the error, we need to clear error in both fields
  React.useEffect(() => {
    if (!hasMinMaxError) {
      // Reset active field when error is resolved
      setActiveField(null);
    }
  }, [hasMinMaxError]);

  return (
    <Grid2 sx={gridStyle}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Checkbox
              checked={check}
              onChange={(e) => onCheckChange(e.target.checked)}
              color="primary"
            />
          }
        />
        <Typography
          align="left"
          whiteSpace="nowrap"
          fontWeight="bold"
          sx={{ width: "150px", color: tokens.colors.fontColour }}
        >
          {label}
        </Typography>
      </Box>
      <Grid2>
        <NumericFormat
          sx={{ width: "200.8px" }}
          disabled={!check}
          allowNegative={false}
          decimalScale={limits.decimalScale}
          fullWidth
          placeholder={`Min (0-${limits.max.toLocaleString()})`}
          onValueChange={handleMinValueChange}
          onFocus={handleMinFocus}
          value={min !== undefined ? min : ""}
          customInput={TextField}
          displayType="input"
          thousandSeparator={true}
          error={!!minError}
          isAllowed={(values) => {
            const { floatValue } = values;
            if (floatValue === undefined) return true;
            return floatValue >= limits.min && floatValue <= limits.max;
          }}
          InputProps={
            minError
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={minError} placement="top">
                        <ErrorOutlineIcon
                          color="error"
                          fontSize="small"
                          sx={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />
      </Grid2>
      <Typography>To</Typography>
      <Grid2>
        <NumericFormat
          disabled={!check}
          allowNegative={false}
          sx={{ width: "200.8px" }}
          decimalScale={limits.decimalScale}
          fullWidth
          placeholder={`Max (0-${limits.max.toLocaleString()})`}
          onValueChange={handleMaxValueChange}
          onFocus={handleMaxFocus}
          value={max !== undefined ? max : ""}
          customInput={TextField}
          displayType="input"
          thousandSeparator={true}
          error={!!maxError}
          isAllowed={(values) => {
            const { floatValue } = values;
            if (floatValue === undefined) return true;
            return floatValue >= limits.min && floatValue <= limits.max;
          }}
          InputProps={
            maxError
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={maxError} placement="top">
                        <ErrorOutlineIcon
                          color="error"
                          fontSize="small"
                          sx={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />
      </Grid2>
    </Grid2>
  );
}

export function MinMaxSelectFilter({
  label,
  options,
  operator,
  onOperatorChange,
  value,
  onValueChange,
  // NEW props for yieldtable preview/behavior
  previewFrom = "",
  previewTo = "",
  showYieldCellTag = false,
}) {
  const isFilterActive = operator !== "" || value !== "";
  
  // Filter out null/empty options
  const filteredOptions = (options || []).filter(option => 
    option && 
    option.rating && 
    option.rating.trim() !== "" && 
    option.value !== null && 
    option.value !== undefined
  );
  
  return (
    <Grid2 sx={gridStyle}>
      <Box sx={{ width: "188px" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography
            align="left"
            whiteSpace="nowrap"
            fontWeight="bold"
            sx={{ lineHeight: 1, color: tokens.colors.fontColour }}
          >
            {label}
          </Typography>
          {showYieldCellTag && (
            <Typography
              component="span"
              sx={{
                fontSize: "12px",
                opacity: 0.7,
                fontWeight: 400,
                lineHeight: 1,
                color: tokens.colors.fontColour85,
              }}
            >
              (Yield Cell)
            </Typography>
          )}
        </Box>
        {(previewFrom || previewTo) && (
          <Typography
            variant="body2"
            sx={{ fontSize: "14px", fontWeight: 500, mt: 0.5, color: tokens.colors.fontColour85 }}
          >
            ({previewFrom ? previewFrom : "..."} to {previewTo ? previewTo : "..."})
          </Typography>
        )}
      </Box>
      <Grid2>
        <Select
          value={operator}
          variant="outlined"
          required
          fullWidth
          onChange={(event) => onOperatorChange(event.target.value)}
          sx={{
            textAlign: "left",
            width: "200.8px",
            "&.MuiSelect-select": { width: "100%" },
            mr: 5,
          }}
          MenuProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            transformOrigin: { vertical: "top", horizontal: "right" },
            PaperProps: { style: { maxHeight: "20ch" } },
          }}
          displayEmpty
          endAdornment={
            <InputAdornment position="end">
              {isFilterActive && (
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Clear both operator and value
                    onOperatorChange("");
                    onValueChange("");
                  }}
                  sx={{ marginRight: "8.5px" }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          }
        >
          <MenuItem value="" sx={{ display: "none" }}>
            Select
          </MenuItem>
          <MenuItem value="gt">{">= Greater than"}</MenuItem>
          <MenuItem value="lt">{"<= Less than"}</MenuItem>
        </Select>
      </Grid2>
      <Grid2 sx={{ position: "relative" }}>
        <Select
          value={value}
          disabled={operator === ""}
          variant="outlined"
          required
          fullWidth
          onChange={(event) => onValueChange(event.target.value)}
          sx={{
            textAlign: "left",
            width: "200.8px",
            "&.MuiSelect-select": { width: "100%" },
          }}
          MenuProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            transformOrigin: { vertical: "top", horizontal: "right" },
            PaperProps: { style: { maxHeight: "20ch" } },
          }}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) return "Select Value";
            const selectedOption = filteredOptions.find(
              (option) => option.value === selected
            );
            return selectedOption ? selectedOption.rating : "Select Max";
          }}
        >
          <MenuItem value="" sx={{ display: "none" }}>
            Select Value
          </MenuItem>
          {filteredOptions.map((option) => (
            <MenuItem key={option.rating} value={option.value}>
              {option.rating}
            </MenuItem>
          ))}
        </Select>
      </Grid2>
    </Grid2>
  );
}

export function BooleanFilter({ label, value, onChange }) {
  const handleCheckboxChange = (e) => {
    if (!e.target.checked) {
      onChange({ target: { value: "" } });
    } else if (value === "") {
      // When enabling the checkbox, default to "No"
      onChange({ target: { value: "0" } });
    }
  };

  return (
    <Grid2 sx={gridStyle}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Checkbox
              checked={value !== ""}
              onChange={handleCheckboxChange}
              color="primary"
            />
          }
        />
        <Typography
          align="left"
          whiteSpace="nowrap"
          fontWeight="bold"
          sx={{ width: "150px", color: tokens.colors.fontColour }}
        >
          {label}
        </Typography>
      </Box>
      <RadioGroup row value={value} onChange={onChange} sx={{ ml: 2 }}>
        <FormControlLabel
          value="1"
          control={<Radio />}
          label="Yes"
          disabled={value === ""}
        />
        <FormControlLabel
          value="0"
          control={<Radio />}
          label="No"
          disabled={value === ""}
        />
      </RadioGroup>
    </Grid2>
  );
}

export function FactoredSecuritiesFilter({ value, onChange, options }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const SC_ALL = "ALL";
  const baseOptions = (options || []).filter((o) => o !== SC_ALL);
  const allOptions = [SC_ALL, ...baseOptions];

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const hasAllSelected = selectedValues.includes(SC_ALL);
  const displayValues = hasAllSelected ? [SC_ALL] : selectedValues;
  const hasSelection = displayValues.length > 0;
  const previewValue = displayValues.join(", ");

  const handleChange = (event, newValue, reason, details) => {
    const changedOption = details?.option;
    const normalized = Array.from(
      new Set((newValue || []).map((v) => String(v).trim()).filter((v) => v))
    );
    let next;
    if (reason === "clear") {
      next = [];
    } else if (changedOption === SC_ALL) {
      next = normalized.includes(SC_ALL) ? [SC_ALL] : [];
    } else {
      const withoutAll = normalized.filter((v) => v !== SC_ALL);
      next = withoutAll.length > 0 ? withoutAll : [];
    }
    onChange(next);
  };

  return (
    <Grid2 sx={gridStyle}>
      <Typography align="left" fontWeight="bold" width="188px" sx={{ color: tokens.colors.fontColour }}>
        Factored Securities
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={allOptions}
        value={displayValues}
        openOnFocus
        onChange={handleChange}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        isOptionEqualToValue={(option, val) => option === val}
        getOptionLabel={(option) => option || ""}
        renderOption={(props, option, { selected }) => (
          <Box
            component="li"
            {...props}
            sx={{ pl: "10px", py: 1, display: "flex", alignItems: "center", fontSize: "16px" }}
          >
            {option !== SC_ALL && (
              <Checkbox
                checked={selected}
                sx={{ width: "20px", height: "20px", padding: 0, marginRight: "8px", borderRadius: 0 }}
                disableRipple
              />
            )}
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: selected ? "bold" : "normal",
                color: selected ? tokens.colors.fontColour : tokens.colors.fontColour85,
              }}
            >
              {option}
            </Typography>
          </Box>
        )}
        renderTags={() => null}
        renderInput={(params) => (
          <Box sx={{ position: "relative", width: "466px" }}>
            <TextField
              {...params}
              InputProps={{ ...params.InputProps }}
              sx={{ width: "466px" }}
            />
            {!isOpen && (
              <Typography
                sx={{
                  position: "absolute",
                  left: "14px",
                  right: "55px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  fontSize: "1rem",
                  color: hasSelection ? tokens.colors.fontColour : "rgba(0,0,0,0.42)",
                }}
              >
                {hasSelection ? previewValue : "Select"}
              </Typography>
            )}
          </Box>
        )}
        sx={{
          width: "466px",
          "& .MuiOutlinedInput-root": {
            height: "56px",
            flexWrap: "nowrap",
            paddingRight: "55px !important",
          },
          "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
            color: isOpen ? "inherit" : "transparent",
            caretColor: isOpen ? "inherit" : "transparent",
            padding: "0 4px !important",
          },
          "& .MuiAutocomplete-endAdornment": {
            top: "50%",
            transform: "translateY(-50%)",
            right: "9px",
          },
        }}
      />
    </Grid2>
  );
}